import { clientApi } from "@/utils/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/queryKeys";
import {
  TCopySavedConditionResDto,
  TCreateSavedConditionReqDto,
  TCreateSavedConditionResDto,
  TDeleteSavedConditionResDto,
  TGetSavedConditionListResDto,
  TUpdateSavedConditionReqDto,
  TUpdateSavedConditionResDto,
} from "@/api/services/main/saved-condition/dtos/saved-condition.dto";

export const getSavedConditionListApi = async (pageType: string): Promise<TGetSavedConditionListResDto> => {
  const { data } = await clientApi.get<TGetSavedConditionListResDto>("/saved-conditions", { params: { pageType } });
  return data;
};

export const createSavedConditionApi = async (payload: TCreateSavedConditionReqDto): Promise<TCreateSavedConditionResDto> => {
  const { data } = await clientApi.post<TCreateSavedConditionResDto>("/saved-conditions", payload);
  return data;
};

export const updateSavedConditionApi = async (id: number, payload: TUpdateSavedConditionReqDto): Promise<TUpdateSavedConditionResDto> => {
  const { data } = await clientApi.patch<TUpdateSavedConditionResDto>(`/saved-conditions/${id}`, payload);
  return data;
};

export const copySavedConditionApi = async (id: number): Promise<TCopySavedConditionResDto> => {
  const { data } = await clientApi.post<TCopySavedConditionResDto>(`/saved-conditions/${id}/copy`);
  return data;
};

export const deleteSavedConditionApi = async (id: number): Promise<TDeleteSavedConditionResDto> => {
  const { data } = await clientApi.delete<TDeleteSavedConditionResDto>(`/saved-conditions/${id}`);
  return data;
};

export const useSavedConditionList = (pageType: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.SAVED_CONDITION.FETCH_LIST, pageType],
    queryFn: () => getSavedConditionListApi(pageType),
    enabled: !!pageType,
  });
};

export const useCreateSavedCondition = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createSavedConditionApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.SAVED_CONDITION.FETCH_LIST] });
    },
  });
};

export const useUpdateSavedCondition = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: TUpdateSavedConditionReqDto }) => updateSavedConditionApi(id, payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.SAVED_CONDITION.FETCH_LIST] });
    },
  });
};

export const useCopySavedCondition = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: copySavedConditionApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.SAVED_CONDITION.FETCH_LIST] });
    },
  });
};

export const useDeleteSavedCondition = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteSavedConditionApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.SAVED_CONDITION.FETCH_LIST] });
    },
  });
};
